import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import * as z from 'zod';

import { useCreateAdditionalServices } from 'src/features/parkingManagement/api/AdditionalServicesAPIs/useCreateAdditionalSerives.ts';
import { useGetParkingAdditionalServices } from 'src/features/parkingManagement/api/AdditionalServicesAPIs/useGetParkingAdditionalServices.ts';
import AdditionalServicesForm from 'src/features/parkingManagement/components/AdditionalServices/AdditionalServicesForm.tsx';
import { useDisplayedAdditionalServicesFormValues } from 'src/features/parkingManagement/providers/AdditionalServicesProvider';
import {
    ADDITIONAL_SERVICES_TYPE,
    ADDITIONAL_SERVICES_USED_FOR,
    type AdditionalServices,
} from 'src/features/parkingManagement/types/additionalServices.ts';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { CURRENCIES } from 'src/features/payStations/types';
import { RESERVATION_TYPE } from 'src/features/reservations/types/reservationType.ts';
import SquareIcon from 'src/registry/components/blocks/SquareIcon.tsx';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast.ts';
import { Button } from 'src/registry/ui/Button.tsx';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
    DialogContent,
    DialogDescription,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages.ts';

export type CreateAdditionalServicesFormSchemaValues = z.infer<typeof createAdditionalServicesFormSchema>;
interface AddAdditionalServicesProps extends DataTableNewEntryProps<AdditionalServices> {}

export const createAdditionalServicesFormSchema = z.object({
    type: z.nativeEnum(ADDITIONAL_SERVICES_TYPE),
    used_for: z.nativeEnum(ADDITIONAL_SERVICES_USED_FOR),
    reservation_types: z.array(z.nativeEnum(RESERVATION_TYPE)).default([]),
    is_optional: z.boolean().optional(),
    is_enabled: z.boolean().optional(),
    name: z.string().min(1),
    description: z.string().optional(),
    currency: z.nativeEnum(CURRENCIES),
    tax_percent: z.string(),
    price_with_tax: z.number(),
    price: z.number().optional(),
    tax_amount: z.number().optional(),
    valid_from: z.coerce.date(),
});

const AddAdditionalServices = ({ onSubmit: onSubmitCallback }: AddAdditionalServicesProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { setDisplayedFormValues } = useDisplayedAdditionalServicesFormValues();

    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<CreateAdditionalServicesFormSchemaValues>({
        resolver: zodResolver(createAdditionalServicesFormSchema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            description: undefined,
            type: undefined,
            used_for: undefined,
            reservation_types: [],
            is_optional: undefined,
            is_enabled: undefined,
            currency: '' as unknown as CURRENCIES,
            tax_percent: undefined,
            price_with_tax: undefined,
            price: undefined,
            tax_amount: undefined,
            valid_from: undefined,
        },
    });

    const { refetch } = useGetParkingAdditionalServices({
        parkingId: parkingId || '',
        config: {
            enabled: !!parkingId,
        },
    });

    const { mutate, isPending } = useCreateAdditionalServices({
        onSuccess: () => {
            void refetch();
            onOpenChange(false);
            form.reset();
            setDisplayedFormValues({
                tax_percent: undefined,
                price_with_tax: undefined,
                price: undefined,
                tax_amount: undefined,
            });
            toast({
                title: formatMessage(parkingManagementMessages.newServiceAdded),
                description: formatMessage(parkingManagementMessages.newServiceAddedSuccessfully),
            });
            if (onSubmitCallback) {
                onSubmitCallback();
            }
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = (values: CreateAdditionalServicesFormSchemaValues) => {
        if (!parkingId) return;
        const { tax_amount, price, ...submitValues } = values;

        mutate({
            parkingId,
            ...submitValues,
        });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button variant="default" className={cn('mb-4 ml-auto')}>
                    <FontAwesomeIcon icon={faPlus} />
                    <FormattedMessage {...parkingManagementMessages.newService} />
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row items-center gap-2">
                        <SquareIcon icon={faPlus} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage {...parkingManagementMessages.newService} />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage {...parkingManagementMessages.addNewServiceDescription} />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AdditionalServicesForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1" onClick={() => form.reset()}>
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={!form.formState.isDirty || !form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        <FormattedMessage {...parkingManagementMessages.newService} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AddAdditionalServices;
